/* =============================================================================
 * M.M.LaFleur — PDP size guide modal.
 *
 * The size chart link opens a dialog in place, because leaving the product to
 * read a chart and coming back loses the colour and size already chosen.
 *
 * WHERE CHART CONTENT COMES FROM
 * ------------------------------
 * The dialog fetches whatever URL the section's Size chart map resolves to and
 * pulls the [data-mm-bare] fragment out of the response. Two producers emit
 * that wrapper — templates/collection.mm-sizechart.liquid (charts held in the
 * theme) and templates/page.mm-bare.liquid (a Shopify page authored by
 * merchandising) — so moving a chart from one to the other is a settings
 * change with no code involved.
 *
 * If the mapped URL does not resolve, the theme endpoint is tried before
 * giving up. That matters because the map is a merchant-editable string and a
 * stale or mistyped value should degrade to the right chart rather than to an
 * error panel.
 *
 * CAPTURE PHASE, AND stopImmediatePropagation
 * -------------------------------------------
 * The trigger is an <a href>, and several document-level listeners compete for
 * it: Barba's link interception, and whatever owns the route being linked to.
 * A bubble listener is not guaranteed to run first, and preventDefault() after
 * a transition has started does nothing — which is why the dialog originally
 * never opened and the browser just followed the link.
 *
 * Capture alone was still not enough. stopPropagation does not silence other
 * listeners bound to the SAME node in the SAME phase, so a second capture
 * listener on document still fired and opened the search overlay behind the
 * dialog. stopImmediatePropagation is what actually guarantees this handler is
 * the only one to see the click.
 *
 * The trigger keeps its real href throughout, so with JS unavailable, or on a
 * middle-click or cmd-click, the link still resolves normally.
 * ========================================================================== */

(function () {
  "use strict";

  /* Charts held in the theme. Used as the fallback whenever the mapped URL
     fails, and it is what the Size chart map should point at by default. */
  var THEME_ENDPOINT = "/collections/all?view=mm-sizechart";

  var CACHE = {};
  var root = null;
  var lastFocus = null;

  var LABEL = /size\s*(chart|guide)/i;

  function resolveTrigger(target) {
    if (!target || !target.closest) return null;

    var explicit = target.closest("[data-mm-sizeguide]");
    if (explicit) return explicit;

    /* The size chart links already rendered by mm_product.liquid are matched by
       position and label: the utility row beside the Size eyebrow, and the
       "View the size chart" link inside the Fit accordion. Both are anchors
       with a real href, so this only ever upgrades a working link into a
       dialog — it never invents a trigger where none existed. */
    var link = target.closest("a[href]");
    if (!link) return null;
    if (!LABEL.test(link.textContent || "")) return null;
    if (link.closest(".mm-pdp-size-links") || link.closest(".mm-acc-body")) return link;
    return null;
  }

  function build() {
    if (root && document.body.contains(root)) return root;

    var scrim = document.createElement("div");
    scrim.className = "mm-sg-scrim mm-scope";
    scrim.setAttribute("data-open", "false");
    scrim.setAttribute("data-mm-sg-scrim", "");
    /* .mm-scope carries an opaque bone background; on a full-viewport overlay
       that would paint over the page instead of washing it. */
    scrim.style.backgroundColor = "rgba(0,0,0,0.4)";

    var panel = document.createElement("div");
    panel.className = "mm-sg mm-scope";
    panel.setAttribute("data-open", "false");
    panel.setAttribute("data-mm-sg", "");
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-label", "Size guide");
    panel.innerHTML =
      '<div class="mm-sg-head">' +
        '<div><h2 data-mm-sg-title>Size guide</h2><p data-mm-sg-sub></p></div>' +
        '<button type="button" class="mm-sg-close" data-mm-sg-close aria-label="Close size guide">' +
          '<svg class="mm-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
          'stroke-width="1.5" stroke-linecap="round" aria-hidden="true">' +
          '<path d="M18 6 6 18M6 6l12 12"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="mm-sg-body" data-mm-sg-body></div>';

    document.body.appendChild(scrim);
    document.body.appendChild(panel);
    root = panel;
    return root;
  }

  function setOpen(open) {
    var panel = document.querySelector("[data-mm-sg]");
    var scrim = document.querySelector("[data-mm-sg-scrim]");
    if (!panel || !scrim) return;
    panel.setAttribute("data-open", String(open));
    scrim.setAttribute("data-open", String(open));
    document.body.classList.toggle("mm-no-scroll", open);
    if (open) {
      var close = panel.querySelector("[data-mm-sg-close]");
      if (close) close.focus();
    } else if (lastFocus && lastFocus.focus) {
      lastFocus.focus();
      lastFocus = null;
    }
  }

  /* The chart is shared by a whole category, so on its own it does not answer
     "will this piece fit me". The fit tips and attribute rows already rendered
     into the Fit accordion are what make it product-specific, so they are
     lifted to the top of the dialog rather than duplicated into a second data
     source that could drift.

     ORDER matches the accordion: the stylist's fit tips and model measurements
     first, then the Fit / Body Type / Length / Sleeve / Neckline rows.

     THE ATTRIBUTE ROWS ARE `.mm-pdp-bullets`. mm_product.liquid renders them
     as <ul class="mm-pdp-bullets">; an earlier revision of this function
     looked for `.mm-pdp-attrs`, a class nothing emits, so the rows were
     silently dropped and only the paragraphs made it into the dialog. Both
     names are accepted so a future rename on either side does not repeat it.
     The rows are re-wrapped in a bare <ul> because the page's own bullet
     styles are scoped to the product section and do not reach the dialog,
     which lives under <body>; .mm-sg-fit ul/li in mm-sizeguide.css styles
     them instead.

     Fit tips entered as plain text rather than rich text arrive as bare text
     inside .mm-pdp-fit-note with no <p>, so that case is wrapped explicitly.

     Read fresh on every open, so flipping colourway or navigating to another
     product shows that product's measurements rather than the first one seen. */
  function productFitMarkup() {
    var acc = null;
    var heads = document.querySelectorAll(".mm-pdp-info .mm-acc-head");
    Array.prototype.forEach.call(heads, function (h) {
      if (acc) return;
      var label = (h.textContent || "").trim().toLowerCase();
      if (label.indexOf("fit") === 0) acc = h.nextElementSibling;
    });
    if (!acc) return "";

    var fitNote = acc.querySelector(".mm-pdp-fit-note");
    var attrs = acc.querySelector(".mm-pdp-bullets, .mm-pdp-attrs");
    var notes = acc.querySelectorAll("p");

    var body = "";

    if (fitNote && !fitNote.querySelector("p") && (fitNote.textContent || "").trim()) {
      body += "<p>" + fitNote.innerHTML + "</p>";
    }

    Array.prototype.forEach.call(notes, function (p) {
      /* Skip the link that opened this dialog — repeating it inside would
         point back at the thing already on screen. */
      if (p.classList.contains("mm-pdp-policy-link")) return;
      if (LABEL.test(p.textContent || "")) return;
      if (!(p.textContent || "").trim()) return;
      body += "<p>" + p.innerHTML + "</p>";
    });

    if (attrs && attrs.children.length) {
      body += "<ul>" + attrs.innerHTML + "</ul>";
    }

    if (!body) return "";
    return '<div class="mm-sg-fit"><span class="mm-eyebrow">This style</span>' + body + "</div>";
  }

  /* A URL that already names a view is asking for a specific template; adding
     view=mm-bare on top would hand Shopify two conflicting views and lose the
     chart. Only the plain Shopify-page route needs the parameter. */
  function bareUrl(url) {
    if (/[?&]view=/.test(url)) return url;
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "view=mm-bare";
  }

  /* The theme endpoint returns every chart in one response, keyed by
     data-chart. Which one this product wants is inferred from the mapped URL,
     since that string is built from the product's own `Size Chart:` tag.
     A Shopify page carries no keys and is used whole. */
  function extract(html, wantKey) {
    var holder = document.createElement("div");
    holder.innerHTML = html;

    var bare = holder.querySelector("[data-mm-bare]");
    if (!bare) return "";

    var keyed = bare.querySelector('[data-chart="' + wantKey + '"]');
    if (keyed) return keyed.innerHTML;

    var firstKeyed = bare.querySelector("[data-chart]");
    if (firstKeyed) return firstKeyed.innerHTML;

    return bare.innerHTML;
  }

  function chartKeyFor(url) {
    return /shoe/i.test(url) ? "shoes" : "clothing";
  }

  function fetchChart(url, wantKey) {
    return fetch(bareUrl(url), { headers: { "X-Requested-With": "fetch" } })
      .then(function (r) {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then(function (html) {
        /* Parsing rather than assigning straight to innerHTML means a 404 that
           quietly returns a full page cannot inject a second header and footer
           into the dialog. */
        var content = extract(html, wantKey);
        if (!content.trim()) throw new Error("empty");
        return content;
      });
  }

  function load(url, body) {
    var wantKey = chartKeyFor(url);
    var cacheKey = url + "#" + wantKey;

    if (CACHE[cacheKey]) {
      body.innerHTML = productFitMarkup() + CACHE[cacheKey];
      return;
    }

    body.innerHTML = '<p class="mm-sg-loading">Loading the size guide…</p>';

    fetchChart(url, wantKey)
      .catch(function () {
        /* The mapped URL did not resolve. Rather than show an error for what is
           usually a stale settings string, fall back to the charts held in the
           theme, which are always present. */
        if (url.indexOf("view=mm-sizechart") !== -1) throw new Error("no fallback");
        return fetchChart(THEME_ENDPOINT, wantKey);
      })
      .then(function (content) {
        CACHE[cacheKey] = content;
        body.innerHTML = productFitMarkup() + content;
      })
      .catch(function () {
        body.innerHTML = productFitMarkup() +
          '<p class="mm-sg-error">No size guide resolved at <code>' + url + '</code>.<br>' +
          'Check the Size chart map in the product section settings.</p>';
      });
  }

  document.addEventListener(
    "click",
    function (event) {
      if (event.target.closest &&
          (event.target.closest("[data-mm-sg-close]") || event.target.closest("[data-mm-sg-scrim]"))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setOpen(false);
        return;
      }

      var trigger = resolveTrigger(event.target);
      if (!trigger) return;

      /* Let modified clicks through so opening in a new tab still works. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;

      var url = trigger.getAttribute("data-mm-sizeguide") || trigger.getAttribute("href");
      if (!url || url === "#") return;

      /* preventDefault stops the browser following the href.
         stopImmediatePropagation stops every other listener — including ones
         bound to document on this same capture phase — from also acting on it,
         which is what kept opening the search overlay behind the dialog. */
      event.preventDefault();
      event.stopImmediatePropagation();

      lastFocus = trigger;

      var panel = build();
      var body = panel.querySelector("[data-mm-sg-body]");
      var title = panel.querySelector("[data-mm-sg-title]");
      var sub = panel.querySelector("[data-mm-sg-sub]");
      if (title) title.textContent = trigger.getAttribute("data-mm-sizeguide-title") || "Size guide";
      if (sub) sub.textContent = trigger.getAttribute("data-mm-sizeguide-sub") || "";

      setOpen(true);
      load(url, body);
    },
    true
  );

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") setOpen(false);
  });
}());
